/**
 * Asset file URLs — the id-in-path convention, for the **editor** only
 *
 * A library reference in a scene document is a plain glTF 2.1 external asset
 * (spec §3.4):
 *
 * ```json
 * files[k] = { "uri": "https://<api>/assets/<guid>/file", "mimeType": "model/gltf+json" }
 * ```
 *
 * `GET /assets/{id}/file` answers 302 to the row's current document, so a
 * generic 2.1 reader needs nothing from us: it follows the redirect, gets a
 * document, and resolves the document's relative `assets/` against wherever it
 * landed. **Rendering never reads the id.** The id is extracted only so an
 * editor can offer "open asset", show the library thumbnail, or use the
 * library name — which is why there is no second carrier: no `extras` id, no
 * custom URL scheme, and nothing here fetches anything.
 *
 * Pure module by contract: no Babylon, no I/O, no scene types. Exported from
 * the `trace-viewer/content` subpath.
 *
 * ### The two matchers, and why there are two
 *
 * {@link parseAssetFileUrl} is the strict one and takes the API base, so it
 * can say *this URL is a reference into **our** library*. Hosts that know
 * their API base (editor, publisher, validator) use it.
 *
 * {@link matchAssetFileUrlPath} is host-agnostic, because **the viewer has no
 * API base at load time** — it is handed a document URL and nothing else. The
 * spec's wording for the load path is deliberately weaker than the validator's:
 * "our loader recognizes the path pattern". So the loader-side derivation
 * (`SceneObject.contentRef`) matches the *shape* and leaves "is that host
 * ours?" to whoever knows.
 *
 * ### The `file:` landmine
 *
 * Never treat a value as absolute without checking both the scheme **and** a
 * non-empty host. `/trace-content/x` and `file:///trace-content/x` parse as
 * absolute URIs with an empty host in most URL parsers, which is how a relative
 * folder path becomes a "URL" that then matches things it must not.
 */

/** RFC 4122 textual GUID, either case. */
const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * `…/assets/{guid}/file`, anchored at the end of a path.
 *
 * Case-insensitive on the route segments as well as the guid: `/assets/…/file`
 * is an ASP.NET route, and ASP.NET routes are matched without regard to case,
 * so `/ASSETS/{guid}/FILE` reaches the same endpoint and is the same reference.
 * A matcher stricter than the server would refuse a document the server
 * accepts — which is the one failure mode a pre-flight must not have.
 */
const ASSET_FILE_PATH_RE = /\/assets\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/file$/i;

/**
 * What an editor knows about a node whose `src` is a library reference.
 *
 * Just the id. Everything else — name, thumbnail, folder, permissions — lives
 * on the row, and fetching the row is the host's job, never the viewer's.
 */
export interface ContentRef {
    /** The library asset's id, lowercase. */
    assetId: string;
    /**
     * The reference's `files[k].name` — the original human file name the
     * referencing document recorded (spec §2.1), when it recorded one.
     *
     * Informational, exactly like the field it comes from: a label an editor
     * can put under a thumbnail before it has loaded the row. The row's name
     * is still the authority; this is what the document happens to remember,
     * and it is absent for a document that never carried it.
     */
    name?: string;
}

/**
 * Parse `uri` as an absolute http(s) URL, or return null.
 *
 * Rejects every non-http(s) scheme (`blob:`, `data:`, `file:`, `builtin:`) and
 * anything with an empty host, which is the landmine above: a bare path is not
 * a URL no matter what a parser makes of it.
 */
function absoluteHttpUrl(uri: string): URL | null {
    if (typeof uri !== 'string' || uri === '') return null;
    let url: URL;
    try {
        url = new URL(uri);
    } catch {
        return null;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    // Belt and braces: an http(s) URL cannot have an empty host in a
    // conforming parser, but the check is the point of the rule.
    if (!url.hostname) return null;
    return url;
}

/**
 * The path prefix an API base contributes, with no trailing slash.
 *
 * A base may carry a path (`https://api.example.com/v1`), and then the
 * reference is `https://api.example.com/v1/assets/{guid}/file`. A base with no
 * path contributes `''`.
 */
function basePathPrefix(base: URL): string {
    return base.pathname.replace(/\/+$/, '');
}

/**
 * The asset id in `uri`, if `uri` is exactly the asset file URL for `apiBase`.
 *
 * Exact means exact: same scheme, host and port as `apiBase`, the base's own
 * path prefix, then `/assets/{guid}/file` and nothing else — no trailing
 * slash, no query, no fragment. The guid matches in either case and comes back
 * **lowercase**, so ids from two writers compare with `===`; the route
 * segments match in either case too, because the API route does
 * (see {@link ASSET_FILE_PATH_RE}).
 *
 * This is the one asset-file-URL matcher in the repo. The manifest pre-flight
 * (`src/export/manifestValidator.ts`) classifies a `files[]` entry as a library
 * reference with exactly this call, so the editor's "open asset" and the
 * publish gate can never disagree about what a reference is.
 *
 * @param uri - a `files[].uri` or a `SceneObject.src`
 * @param apiBase - the API origin, with or without a path prefix or trailing slash
 * @returns the lowercase asset id, or null
 */
export function parseAssetFileUrl(uri: string, apiBase: string): string | null {
    const url = absoluteHttpUrl(uri);
    if (!url) return null;

    const base = absoluteHttpUrl(apiBase);
    if (!base) return null;

    // `origin` folds the default port in for us: `https://h:443` === `https://h`.
    if (url.origin !== base.origin) return null;

    // A reference carries no query and no fragment. One that does is not the
    // convention form — and an editor that guessed an id out of it would offer
    // "open asset" for a URL it cannot reproduce.
    if (url.search !== '' || url.hash !== '') return null;

    // The base's own path prefix, compared case-insensitively for the same
    // reason the route segments are (see ASSET_FILE_PATH_RE).
    const prefix = basePathPrefix(base);
    if (prefix && !url.pathname.toLowerCase().startsWith(`${prefix.toLowerCase()}/`)) return null;

    const rest = url.pathname.slice(prefix.length);
    const m = /^\/assets\/([^/]+)\/file$/i.exec(rest);
    if (!m) return null;
    if (!GUID_RE.test(m[1])) return null;

    return m[1].toLowerCase();
}

/** Whether `uri` is the asset file URL for `apiBase`. See {@link parseAssetFileUrl}. */
export function isAssetFileUrl(uri: string, apiBase: string): boolean {
    return parseAssetFileUrl(uri, apiBase) !== null;
}

/**
 * Build the asset file URL for `assetId` under `apiBase`.
 *
 * Round-trips through {@link parseAssetFileUrl} with the same base. Throws on a
 * base that is not an absolute http(s) URL or on an id that is not a guid —
 * both are programming errors, and a silently malformed reference would only
 * surface as a document the server rejects on write (spec §2.5).
 */
export function assetFileUrl(apiBase: string, assetId: string): string {
    const base = absoluteHttpUrl(apiBase);
    if (!base) throw new Error(`assetFileUrl: apiBase must be an absolute http(s) URL, got "${apiBase}"`);
    if (!GUID_RE.test(assetId)) throw new Error(`assetFileUrl: assetId must be a guid, got "${assetId}"`);
    return `${base.origin}${basePathPrefix(base)}/assets/${assetId.toLowerCase()}/file`;
}

/**
 * The asset id in `uri` by **path shape alone**, for hosts with no API base.
 *
 * This is what the load path uses (spec §3.4: "our loader recognizes the path
 * pattern"). It still requires an absolute http(s) URL with a real host — a
 * relative folder file (`assets/<hash>.glb`) or a `blob:` src is never a
 * library reference — but it does not care *which* host, because a viewer
 * loading a document has not been told which API it came from.
 *
 * Query and fragment are rejected here too: the convention form has neither,
 * and matching loosely would let a signed media URL that happens to end
 * `/assets/<guid>/file` masquerade as a library reference.
 *
 * @returns the lowercase asset id, or null
 */
export function matchAssetFileUrlPath(uri: string): string | null {
    const url = absoluteHttpUrl(uri);
    if (!url) return null;
    if (url.search !== '' || url.hash !== '') return null;

    const m = ASSET_FILE_PATH_RE.exec(url.pathname);
    return m ? m[1].toLowerCase() : null;
}
